import {
  BottomGraphic,
  CoverageSection,
  FadeIn,
  FaqSection,
  Footer,
  GradientBg,
  Header,
  InsuranceSection,
  PageHeader,
  WhoNeedsInsuranceSection,
  WhyIsInsuranceImportant,
} from "../components"
import { ProfessionalLiabilitySvg } from "../components/svg"
import {
  ExclamationCircleIcon,
  InboxIcon,
  SpeakerphoneIcon,
} from "@heroicons/react/outline"
import type { NextPage } from "next"
import Head from "next/head"
import { NextSeo } from "next-seo"
import { useRouter } from "next/router"

const faqs = [
  {
    question: "Is professional liability insurance required by law?",
    answer:
      "Professional liability requirements vary by state and industry. However, your business can be exposed to high risks of financial loss if you are not properly protected. You may also lose potential clients and customers if you are asked to show proof of insurance and are not able to provide evidence of coverage. In short, although it might not be legally required, a professional liability policy is crucial to protect your business from everyday risks.",
  },
  {
    question: "How do I show proof I have a professional liability policy?",
    answer: `Once you've purchased your professional liability policy, a Certificate of Insurance (“COI”) will be sent to you and can be presented to any party that requires proof of coverage. You can also request that the COI be sent to any third-party that requests verification of insurance.`,
  },
  {
    question:
      "What is an additional insured? Can I add one to my professional liability?",
    answer:
      "An additional insured is any party that wants to be listed as a covered party under your policy. In certain cases, an additional insured might be a vendor, marketplace, landlord or any other third-party whom which you conduct business with.",
  },
  {
    question: "Does professional liability cover my employees?",
    answer:
      "Yes, employees directly related to your business will be covered under a professional liability policy.",
  },
  {
    question:
      "Is professional liability insurance the same as errors and omissions (E&O) insurance?",
    answer:
      "Yes, professional liability insurance and errors and omissions (E&O) insurance are interchangeable terms and cover the same exact risks.",
  },
]
const coverageItems = [
  {
    title: "Negligence",
    description:
      "A client may claim that your business was negligent in handling a project or assignment, and your business will be responsible for covering legal costs and damages associated with the accusation.",
    icon: SpeakerphoneIcon,
  },
  {
    title: "Unfulfilled Task",
    description:
      "A client may accuse your business of not fulfilling a task, in which case you may be required to pay consequential damages.",
    icon: InboxIcon,
  },
  {
    title: "Mistakes",
    description:
      "A client may accuse your business of making professional errors, in which case you may be required to reimburse damages or hire legal counsel to defend your stance.",
    icon: ExclamationCircleIcon,
  },
]

const ProfessionalLiability: NextPage = () => {
  const router = useRouter()
  const canonicalUrl = (
    `https://www.coverdash.com` + (router.asPath === "/" ? "" : router.asPath)
  ).split("?")[0]
  return (
    <>
      <NextSeo
        title="Professional Liability"
        canonical={canonicalUrl}
        openGraph={{
          url: canonicalUrl,
          title: "Professional Liability",
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Is professional liability insurance required by law?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Professional liability requirements vary by state and industry. However, your business can be exposed to high risks of financial loss if you are not properly protected. You may also lose potential clients and customers if you are asked to show proof of insurance and are not able to provide evidence of coverage. In short, although it might not be legally required, a professional liability policy is crucial to protect your business from everyday risks."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I show proof I have a professional liability policy?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Once you've purchased your professional liability policy, a Certificate of Insurance (COI) will be sent to you and can be presented to any party that requires proof of coverage. You can also request that the COI be sent to any third-party that requests verification of insurance."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What is an additional insured? Can I add one to my professional liability?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "An additional insured is any party that wants to be listed as a covered party under your policy. In certain cases, an additional insured might be a vendor, marketplace, landlord or any other third-party whom which you conduct business with."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Does professional liability cover my employees?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, employees directly related to your business will be covered under a professional liability policy."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is professional liability insurance the same as errors and omissions (E&O) insurance?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, professional liability insurance and errors and omissions (E&O) insurance are interchangeable terms and cover the same exact risks."
                  }
                }
              ]
            })
          }}
        />
      </Head>
      <GradientBg />
      <Header />
      <PageHeader
        title="Professional liability"
        image={
          <>
            <ProfessionalLiabilitySvg className="hidden sm:block mx-auto max-w-4xl w-full mt-8 md:mt-0" />
            <img
              src="/img/sb2.jpg"
              className="sm:hidden w-full max-w-4xl shadow rounded overflow-hidden"
            />
          </>
        }
      />

      {/* Section-1 */}
      <div className="max-w-7xl mx-auto pb-8 lg:py-20 px-4 sm:px-6 lg:pg-8 text-center">
        <FadeIn>
          <h2 className="max-w-5xl mx-auto text-5xl text-alt font-extrabold my-7">
            What is <span className="text-primary">professional liability</span>{" "}
            insurance?
          </h2>
        </FadeIn>
        <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
          Professional liability, also known as Errors and Omissions (E&O)
          Insurance, covers your business from customer legal disputes arising
          from professional negligence, mistakes, or missed client deadlines.
          Costs associated with defending your business, regardless of whether
          the claims are valid, can be covered under this policy. Certain
          industries or clients require proof of professional liability
          insurance to conduct business.
        </p>
      </div>

      {/* Section-2 */}
      <CoverageSection title="professional liability" items={coverageItems} />

      {/* Section-3 */}
      <WhoNeedsInsuranceSection
        title="professional liability"
        description="Any business that provides any consulting or professional services, should consider sourcing protection from common business risks in the event of potential negligence, errors or third-party accusations. If your business is at risk of making a professional error or omission you should consider a professional liability policy."
      />

      {/* Section-4 */}
      <WhyIsInsuranceImportant>
        <FadeIn>
          <h2 className="max-w-5xl mx-auto text-5xl text-alt font-extrabold my-7">
            Why is <span className="text-primary">professional liability</span>{" "}
            important?
          </h2>
        </FadeIn>
        <p className="mt-4 text-lg text-gray-500">
          Regardless of industry or size, a professional liability policy can
          protect your business from common financial risks that can be incurred
          as a result of providing professional services. Certain states and
          various clients require proof of professional liability insurance in
          order to allow businesses to transact.
        </p>
      </WhyIsInsuranceImportant>

      {/* Section-5 */}
      <InsuranceSection />

      {/* Section-5 */}
      <FaqSection title="Professional liability insurance" faqs={faqs} />

      <BottomGraphic />
      <Footer />
    </>
  )
}

export default ProfessionalLiability
